import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, RotateCcw, Activity } from "lucide-react";

export const SignupHealthCard = () => {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [resetting, setResetting] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["pre-reg-health"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("student_pre_registrations")
        .select("id, form_iv_index, full_name, class, course, mobile_number, onboarding_status, otp_expires_at, created_at")
        .in("onboarding_status", ["pre_registered", "otp_pending", "active"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const counts = (data ?? []).reduce(
    (acc, r) => {
      acc[r.onboarding_status] = (acc[r.onboarding_status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const problematic = (data ?? []).filter(
    (r) =>
      r.onboarding_status !== "active" &&
      (!r.full_name || !r.class || !r.course || r.onboarding_status === "otp_pending")
  );

  const resetRow = useMutation({
    mutationFn: async (id: string) => {
      setResetting(id);
      const { error } = await supabase
        .from("student_pre_registrations")
        .update({ onboarding_status: "pre_registered", otp_code: null, otp_expires_at: null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "OTP state reset", description: "Student can retry signup now." });
      qc.invalidateQueries({ queryKey: ["pre-reg-health"] });
    },
    onError: (e: any) => toast({ title: "Reset failed", description: e.message, variant: "destructive" }),
    onSettled: () => setResetting(null),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          Signup Health
        </CardTitle>
        <CardDescription className="text-xs">
          Pre-registration pipeline status. Reset stuck rows to let students retry signup.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary">Active: {counts.active || 0}</Badge>
          <Badge variant="outline">Pre-registered: {counts.pre_registered || 0}</Badge>
          <Badge variant={counts.otp_pending ? "destructive" : "outline"}>
            OTP Pending: {counts.otp_pending || 0}
          </Badge>
        </div>

        {isLoading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : problematic.length === 0 ? (
          <p className="text-xs text-muted-foreground">No stuck or incomplete pre-registrations. 🎉</p>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-3.5 w-3.5" />
              {problematic.length} row(s) need attention (missing data or stuck OTP).
            </div>
            <div className="table-responsive">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Form IV Index</TableHead>
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Class</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Issue</TableHead>
                    <TableHead className="text-xs w-24">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {problematic.map((r) => {
                    const issues: string[] = [];
                    if (!r.full_name) issues.push("name");
                    if (!r.class) issues.push("class");
                    if (!r.course) issues.push("course");
                    if (r.onboarding_status === "otp_pending") issues.push("otp stuck");
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="font-mono text-xs">{r.form_iv_index}</TableCell>
                        <TableCell className="text-xs">{r.full_name || <span className="text-destructive">—</span>}</TableCell>
                        <TableCell className="text-xs">{r.class || <span className="text-destructive">—</span>}</TableCell>
                        <TableCell className="text-xs">
                          <Badge variant={r.onboarding_status === "otp_pending" ? "destructive" : "outline"} className="text-[10px]">
                            {r.onboarding_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{issues.join(", ")}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={resetting === r.id}
                            onClick={() => resetRow.mutate(r.id)}
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Reset
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
